import { formatHostedConfigMessage } from '@hormigadormida/shared-runtime/hosted-config.js'
import { t, escapeHtml, formatTime } from './portal-utils.js'
import { portalStyles } from './portal-styles.js'

export function renderApprovalItem(approval: Record<string, unknown>): string {
  const approvalId = t(approval.request_id || approval.id)
  const status = t(approval.status || 'pending')
  return `
    <div class="op-item" data-approval-id="${escapeHtml(approvalId)}">
      <div class="op-item-row">
        <div class="op-item-title">${escapeHtml(t(approval.operation) || 'Approval request')}</div>
        <span class="op-badge ${escapeHtml(status)}">${escapeHtml(status)}</span>
      </div>
      <div class="op-item-meta">
        ${escapeHtml(t(approval.platform) || 'system')} • ${escapeHtml(t(approval.mission_id) || 'workspace')} • ${escapeHtml(formatTime(approval.created_at))}
      </div>
      <div class="op-item-meta">${escapeHtml(t(approval.notes) || t(approval.policy_reason) || 'Waiting on an operator decision.')} </div>
      ${status === 'pending' ? `
        <div class="op-actions">
          <button class="op-btn primary" data-approval-action="approved">Approve</button>
          <button class="op-btn" data-approval-action="rejected">Reject</button>
        </div>
      ` : ''}
    </div>
  `
}

export function renderList(items: any[], renderItem: (item: any) => string, emptyText: string, emptyOptions: Record<string, any> = {}) {
  if (!items.length) {
    return renderEmptyCard(emptyText, emptyOptions)
  }
  return items.map(renderItem).join('')
}

/**
 * Warm empty card with a Miga cameo + drifting marginalia ants.
 *
 * `emptyOptions.tone` accepts 'neutral' | 'info' | 'success' to match
 * the cockpit's greenfield empty cards. The ants are decorative — they
 * collapse to display:none under prefers-reduced-motion via inline CSS.
 */
/**
 * @param {string} emptyText
 * @param {{ tone?: string, headline?: string }} [emptyOptions]
 */
function renderEmptyCard(emptyText: string, { tone = 'neutral', headline }: { tone?: string, headline?: string } = {}) {
  const safeText = escapeHtml(emptyText)
  const safeHeadline = headline ? escapeHtml(headline) : ''
  const toneClass = `op-empty--${tone}`
  return `
    <div class="op-empty op-empty--warm ${toneClass}" role="note">
      <div class="op-empty-stage" aria-hidden="true">
        <span class="op-empty-halo"></span>
        <img class="op-empty-miga" src="/miga.svg" width="44" height="44" alt="" />
        <span class="op-empty-trail op-empty-trail--a"></span>
        <span class="op-empty-trail op-empty-trail--b"></span>
      </div>
      <div class="op-empty-body">
        ${safeHeadline ? `<div class="op-empty-headline">${safeHeadline}</div>` : ''}
        <div class="op-empty-text">${safeText}</div>
      </div>
    </div>
  `
}

export function renderPortalSkeleton() {
  return `
    ${portalStyles()}
    <div class="operator-portal">
      <div class="op-shell">
        <section class="op-header">
          <div class="op-eyebrow">Reachable Operator Portal</div>
          <div class="op-header-main">
            <div>
              <h1 class="op-title">The workspace stays honest across every surface.</h1>
              <div class="op-subtitle">
                Attention, approvals, briefs, and execution history are now shared through Firestore so Signal, Telegram, MCP, and the cockpit all point at the same control plane.
              </div>
            </div>
            <div class="op-workspace-switch">
              <label for="operator-workspace-select">Workspace</label>
              <div class="op-toolbar">
                <select id="operator-workspace-select"></select>
                <button class="op-btn" id="op-invite-toggle" type="button">Invite teammate</button>
              </div>
            </div>
          </div>
          <div class="op-invite-panel hidden" id="op-invite-panel">
            <div>
              <div class="op-list-title" style="font-size:1.05rem;">Create workspace invite</div>
              <div class="op-list-subtitle">Generate a single-use or limited-use invite code for another operator.</div>
            </div>
            <div class="op-invite-grid">
              <div class="op-field">
                <label for="op-invite-role">Role</label>
                <select id="op-invite-role">
                  <option value="operator">Operator</option>
                  <option value="reviewer">Reviewer</option>
                  <option value="observer">Observer</option>
                </select>
              </div>
              <div class="op-field">
                <label for="op-invite-expiry">Expiry</label>
                <select id="op-invite-expiry">
                  <option value="86400000">24 hours</option>
                  <option value="604800000" selected>7 days</option>
                  <option value="2592000000">30 days</option>
                </select>
              </div>
              <div class="op-field">
                <label for="op-invite-uses">Max uses</label>
                <input id="op-invite-uses" type="number" min="1" step="1" value="1" />
              </div>
            </div>
            <div class="op-actions">
              <button class="op-btn primary" id="op-invite-create" type="button">Create invite</button>
              <button class="op-btn" id="op-invite-close" type="button">Close</button>
            </div>
            <div id="op-invite-feedback"></div>
          </div>
          <div class="op-metrics">
            <article class="op-card">
              <div class="op-card-label">Health</div>
              <div class="op-health">
                <div class="op-health-score" id="op-health-score">—</div>
                <div class="op-health-delta" id="op-health-delta">Waiting for snapshot</div>
              </div>
            </article>
            <article class="op-card">
              <div class="op-card-label">Attention</div>
              <div class="op-card-value" id="op-attention-count">0</div>
              <div class="op-card-note" id="op-attention-note">No watch alerts yet.</div>
            </article>
            <article class="op-card">
              <div class="op-card-label">Pending Approvals</div>
              <div class="op-card-value" id="op-pending-approvals">0</div>
              <div class="op-card-note">Every surface resolves the same queue.</div>
            </article>
            <article class="op-card">
              <div class="op-card-label">Execution Trail</div>
              <div class="op-card-value" id="op-execution-count">0</div>
              <div class="op-card-note" id="op-execution-note">No recorded executions yet.</div>
            </article>
          </div>
        </section>
        <section class="op-grid">
          <div class="op-column">
            <article class="op-list-card">
              <div class="op-list-head">
                <div>
                  <h2 class="op-list-title">Ask Miga</h2>
                  <div class="op-list-subtitle">Grounded answers from workspace truth, with fresh operator-engine reads when the hosted path is available</div>
                </div>
              </div>
              <form class="op-miga-form" id="op-miga-form">
                <textarea class="op-input" id="op-miga-question" placeholder="Ask about ROAS, CTR, CPA, spend, setup state, or what changed."></textarea>
                <div class="op-actions">
                  <button class="op-btn primary" id="op-miga-submit" type="submit">Ask Miga</button>
                </div>
              </form>
              <div class="op-miga-output" id="op-miga-output">Ask a workspace question to get a grounded answer here.</div>
              <div class="op-miga-meta" id="op-miga-meta">Hosted portal uses cached workspace truth by default and upgrades to fresh operator-engine reads when that path is configured.</div>
            </article>
            <article class="op-list-card">
              <div class="op-list-head">
                <div>
                  <h2 class="op-list-title">Attention Feed</h2>
                  <div class="op-list-subtitle">Live view of what needs operator focus right now</div>
                </div>
              </div>
              <div id="op-attention-feed"></div>
            </article>
            <article class="op-list-card">
              <div class="op-list-head">
                <div>
                  <h2 class="op-list-title">Brief Timeline</h2>
                  <div class="op-list-subtitle">The latest push-ready notes and morning briefs</div>
                </div>
              </div>
              <div id="op-brief-timeline"></div>
            </article>
          </div>
          <div class="op-column">
            <article class="op-list-card">
              <div class="op-list-head">
                <div>
                  <h2 class="op-list-title">Approval Inbox</h2>
                  <div class="op-list-subtitle">Pending decisions with actor-aware audit</div>
                </div>
              </div>
              <div id="op-approval-inbox"></div>
            </article>
            <article class="op-list-card">
              <div class="op-list-head">
                <div>
                  <h2 class="op-list-title">Execution Trail</h2>
                  <div class="op-list-subtitle">Recent executed or failed actions</div>
                </div>
              </div>
              <div id="op-execution-trail"></div>
            </article>
          </div>
        </section>
      </div>
    </div>
  `
}

export function renderAttentionItems(items: Record<string, unknown>[]): string {
  return renderList(
    items,
    (item) => `
      <div class="op-item">
        <div class="op-item-row">
          <div class="op-item-title">${escapeHtml(t(item.title) || t(item.headline) || 'Watch signal')}</div>
          <span class="op-badge ${escapeHtml(t(item.severity) || 'pending')}">${escapeHtml(t(item.severity) || 'signal')}</span>
        </div>
        <div class="op-item-meta">${escapeHtml(t(item.summary) || t(item.whyItMatters) || 'No summary available.')}</div>
        <div class="op-item-meta">${escapeHtml(t(item.provider) || 'workspace')} • ${escapeHtml(formatTime(item.occurredAt || item.detectedAt || item.created_at))}</div>
      </div>
    `,
    'No active watch signals are in the workspace snapshot.',
    { tone: 'neutral', headline: 'Miga is watching.' }
  )
}

export function renderBriefItems(items: Record<string, unknown>[]): string {
  return renderList(
    items,
    (item) => `
      <div class="op-item">
        <div class="op-item-row">
          <div class="op-item-title">${escapeHtml(t(item.headline) || 'Operator brief')}</div>
          <span class="op-badge ${escapeHtml(t(item.deliveryStatus || 'pending'))}">${escapeHtml(t(item.kind) || 'brief')}</span>
        </div>
        <div class="op-item-meta">${escapeHtml(t(item.wrapperText) || t(item.displayText) || 'No brief body recorded.')}</div>
        <div class="op-item-meta">${escapeHtml(formatTime(item.createdAt))}${item.link ? ` • <a href="${sanitizeLink(item.link)}" target="_blank" rel="noreferrer">Open note</a>` : ''}</div>
      </div>
    `,
    'No briefs have been written into Firestore for this workspace yet.',
    { tone: 'info', headline: 'The first brief lands after one watch cycle.' }
  )
}

export function renderExecutionItems(items: Record<string, unknown>[]): string {
  return renderList(
    items,
    (item) => `
      <div class="op-item">
        <div class="op-item-row">
          <div class="op-item-title">${escapeHtml(t(item.actionType) || 'Execution')}</div>
          <span class="op-badge ${escapeHtml(t(item.status) || 'pending')}">${escapeHtml(t(item.status) || 'unknown')}</span>
        </div>
        <div class="op-item-meta">${escapeHtml(t(item.summary) || 'Execution recorded.')}</div>
        <div class="op-item-meta">${escapeHtml(t(item.provider) || 'system')} • ${escapeHtml(formatTime(item.executedAt || item.updatedAt))}</div>
      </div>
    `,
    'No execution records are available yet.',
    { tone: 'neutral', headline: 'Nothing has been executed yet.' }
  )
}

function renderMigaPill(label: string, className: string): string {
  return `<span class="op-miga-pill ${escapeHtml(className)}">${escapeHtml(label)}</span>`
}

const MACHINE_READABLE_REASON_CODE_PATTERN = /^[a-z0-9]+(?:_[a-z0-9]+)*$/

function normalizeProviderList(providers: unknown = []): string[] {
  return [...new Set(
    (Array.isArray(providers) ? providers : [])
      .map((provider) => t(provider).toLowerCase())
      .filter(Boolean)
  )]
}

function inferModeFromScope(scope: any = {}) {
  const explicitMode = t(scope.effectiveAggregationMode) || t(scope.aggregationMode)
  if (explicitMode) {
    return explicitMode.toLowerCase()
  }
  return ''
}

function normalizeMigaMode(result: any = {}) {
  const resolvedScope = result?.resolvedScope && typeof result.resolvedScope === 'object'
    ? result.resolvedScope
    : {}
  const explicitMode = t(result.mode).toLowerCase()
  if (explicitMode) {
    return explicitMode
  }

  const scopeMode = inferModeFromScope(resolvedScope)
  if (scopeMode) {
    return scopeMode
  }

  const compareContract = result?.compareContract && typeof result.compareContract === 'object'
    ? result.compareContract
    : {}
  const comparePartitions = Array.isArray(compareContract.providerPartitions)
    ? compareContract.providerPartitions
    : []
  if (comparePartitions.length >= 2 || (Array.isArray(compareContract.deltas) && compareContract.deltas.length > 0)) {
    return 'compare'
  }

  const unifiedPortfolio = result?.unifiedPortfolio && typeof result.unifiedPortfolio === 'object'
    ? result.unifiedPortfolio
    : {}
  const providerContributions = Array.isArray(unifiedPortfolio.providerContributions)
    ? unifiedPortfolio.providerContributions
    : []
  if (providerContributions.length >= 2) {
    return 'unified'
  }

  const providersFromScope = new Set([
    ...normalizeProviderList(resolvedScope.providers),
    ...normalizeProviderList(resolvedScope.effectiveProviders)
  ])
  if (providersFromScope.size > 1) {
    return 'unified'
  }
  if (providersFromScope.size === 1) {
    return 'single'
  }

  const providerStatuses = result?.providerStatuses && typeof result.providerStatuses === 'object'
    ? result.providerStatuses
    : {}
  const providerStatusCount = Object.keys(providerStatuses).length
  if (providerStatusCount > 1) {
    return 'unified'
  }
  if (providerStatusCount === 1) {
    return 'single'
  }

  return 'single'
}

function normalizeMigaState(result: any = {}, mode = 'single') {
  const state = t(result.state).toLowerCase()
  if (state) {
    return state
  }
  const status = t(result.status).toLowerCase()
  if (status === 'grounded' && mode === 'unified') {
    return 'unified'
  }
  return status || 'blocked'
}

function normalizeProviderStatusEntries(result: any = {}): { provider: string; status: string; reasonCode: string; freshness?: string }[] {
  const statuses = result?.providerStatuses && typeof result.providerStatuses === 'object'
    ? result.providerStatuses
    : {}
  return (Object.entries(statuses) as [string, unknown][])
    .map(([provider, rawStatus]) => {
      const statusRecord = (rawStatus && typeof rawStatus === 'object' ? rawStatus : {}) as Record<string, unknown>
      const providerName = t(provider).toLowerCase()
      const status = t(statusRecord.status).toLowerCase() || 'missing'
      if (!providerName) {
        return null
      }
      return {
        provider: providerName,
        status,
        reasonCode: t(statusRecord.reasonCode) || 'unknown_warning',
        freshness: t(statusRecord.freshness),
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry != null)
}

function normalizeDerivedProviderState(status: string, freshness: string): string {
  if (status === 'missing') {
    return 'missing'
  }
  if (status === 'blocked') {
    return 'blocked'
  }
  if (freshness === 'cached' || freshness === 'stale_fallback' || freshness === 'stale') {
    return 'stale'
  }
  if (status === 'partial') {
    return 'partial'
  }
  return status || 'missing'
}

function normalizeDerivedReasonCode(entry: Record<string, unknown> = {}): string {
  const provider = t(entry.provider).toLowerCase()
  const normalizedReasonCode = t(entry.reasonCode).toLowerCase()
  if (entry.providerState === 'blocked') {
    return provider ? `${provider}_provider_blocked` : 'provider_blocked'
  }
  if (MACHINE_READABLE_REASON_CODE_PATTERN.test(normalizedReasonCode)) {
    return normalizedReasonCode
  }
  if (entry.providerState === 'missing') {
    return 'provider_unavailable'
  }
  if (entry.providerState === 'stale') {
    return 'provider_stale'
  }
  if (entry.providerState === 'partial') {
    return 'provider_partial'
  }
  return 'unknown_warning'
}

function buildDerivedWarningImpact(entry: Record<string, unknown> = {}, mode = 'single'): string {
  const provider = t(entry.provider).toLowerCase()
  const label = provider ? provider.toUpperCase() : 'PROVIDER'
  if (entry.providerState === 'missing') {
    return `${label} evidence is missing for ${mode} mode; confidence is reduced because coverage is partial.`
  }
  if (entry.providerState === 'blocked') {
    return `${label} evidence is blocked for ${mode} mode; confidence is reduced because this answer excludes ${label} data.`
  }
  if (entry.providerState === 'stale') {
    return `${label} evidence is stale for ${mode} mode; confidence is reduced until a fresh provider read succeeds.`
  }
  return `${label} evidence is partial for ${mode} mode; confidence is reduced for provider-level comparisons.`
}

function deriveWarningEntriesFromProviderStatuses(providerEntries: { status: string; freshness?: string; provider: string }[] = [], mode = 'single'): { provider: string; reasonCode: string; freshness?: string; impact: string }[] {
  return providerEntries
    .filter((entry) => entry.status !== 'reachable')
    .map((entry) => {
      const providerState = normalizeDerivedProviderState(
        t(entry.status).toLowerCase(),
        t(entry.freshness).toLowerCase()
      )
      return {
        provider: entry.provider,
        reasonCode: normalizeDerivedReasonCode({ ...entry, providerState }),
        freshness: entry.freshness,
        impact: buildDerivedWarningImpact({ ...entry, providerState }, mode),
      }
    })
}

function normalizeWarningEntries(result: any = {}, providerEntries: any[] = [], mode = 'single'): { provider: string; reasonCode: string; impact?: string; freshness?: string }[] {
  const warningBundle = result?.warningBundle && typeof result.warningBundle === 'object'
    ? result.warningBundle
    : {}
  const rawEntries: unknown[] = Array.isArray(warningBundle.entries)
    ? warningBundle.entries
    : []
  const entries = rawEntries
      .map((entry) => {
        const normalizedEntry = (entry && typeof entry === 'object' ? entry : {}) as Record<string, unknown>
        const provider = t(normalizedEntry.provider).toLowerCase()
        if (!provider) {
          return null
        }
        return {
          provider,
          reasonCode: t(normalizedEntry.reasonCode) || 'unknown_warning',
          impact: t(normalizedEntry.impact),
          freshness: t(normalizedEntry.freshness),
        }
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry != null)
  if (entries.length > 0) {
    return entries
  }
  return deriveWarningEntriesFromProviderStatuses(providerEntries, mode)
}

function isMissingProviderWarning(entry: { provider?: string; reasonCode?: string } | null | undefined, providerEntries: { provider: string; status?: string }[] = []): boolean {
  const provider = t(entry?.provider).toLowerCase()
  const reasonCode = t(entry?.reasonCode).toLowerCase()
  const providerState = providerEntries.find((candidate) => candidate.provider === provider)?.status || ''
  return (
    providerState === 'missing'
    || reasonCode.includes('missing')
    || reasonCode.includes('unavailable')
  )
}

function shouldSuppressMissingProviderWarnings(state: string, status: string, mode: string): boolean {
  return state === 'unified' || (status === 'grounded' && mode === 'unified')
}

export function renderMigaState(result: any = {}) {
  const status = t(result.status) || 'grounded'
  const freshness = t(result.freshness) || 'cached'
  const mode = normalizeMigaMode(result)
  const state = normalizeMigaState(result, mode)
  const answeredAt = t(result.answeredAt)
  const warnings = Array.isArray(result.warnings) ? result.warnings.map((entry: unknown) => t(entry)).filter(Boolean) : []
  const providerEntries = normalizeProviderStatusEntries(result)
  const warningEntries = normalizeWarningEntries(result, providerEntries, mode)
  const displayWarningEntries = shouldSuppressMissingProviderWarnings(state, status.toLowerCase(), mode)
    ? warningEntries.filter((entry) => !isMissingProviderWarning(entry, providerEntries))
    : warningEntries
  const evidence = Array.isArray(result.evidence) ? result.evidence : []
  const source = result.sourceResolution && typeof result.sourceResolution === 'object'
    ? result.sourceResolution
    : null

  return `
    <div class="op-miga-state">
      <div class="op-miga-answer">${escapeHtml(t(result.content) || 'No grounded answer was returned.')}</div>
      <div class="op-miga-pills">
        ${renderMigaPill(status.replaceAll('_', ' '), status)}
        ${renderMigaPill(mode.replaceAll('_', ' '), mode)}
        ${renderMigaPill(state.replaceAll('_', ' '), state)}
        ${renderMigaPill(freshness.replaceAll('_', ' '), freshness)}
        ${answeredAt ? renderMigaPill(`answered ${formatTime(answeredAt)}`, 'answered') : ''}
      </div>
      ${t(result.blockedReason)
        ? `<div class="op-miga-note"><strong>Blocked:</strong> ${escapeHtml(t(result.blockedReason))}</div>`
        : ''}
      ${t(result.clarificationPrompt)
        ? `<div class="op-miga-note"><strong>Need clarification:</strong> ${escapeHtml(t(result.clarificationPrompt))}</div>`
        : ''}
      ${source && (t(source.matchedSourceType) || t(source.unresolvedReason))
        ? `<div class="op-miga-note"><strong>Source:</strong> ${escapeHtml(t(source.matchedSourceType || source.unresolvedReason).replaceAll('_', ' '))}${t(source.matchedSourceId) ? ` • ${escapeHtml(t(source.matchedSourceId))}` : ''}</div>`
        : ''}
      ${warnings.length > 0
        ? `<div class="op-miga-note"><strong>Warnings</strong><ul>${warnings.slice(0, 4).map((warning: string) => `<li>${escapeHtml(warning)}</li>`).join('')}</ul></div>`
        : ''}
      ${providerEntries.length > 0
        ? `<div class="op-miga-note"><strong>Provider states</strong><ul>${providerEntries.map((entry: { provider: string; status: string; reasonCode?: string; freshness?: string }) => `<li>${escapeHtml(entry.provider.toUpperCase())}: ${escapeHtml(entry.status)}${entry.reasonCode ? ` • ${escapeHtml(entry.reasonCode)}` : ''}${entry.freshness ? ` • ${escapeHtml(entry.freshness)}` : ''}</li>`).join('')}</ul></div>`
        : ''}
      ${displayWarningEntries.length > 0
        ? `<div class="op-miga-note"><strong>Provider warnings</strong><ul>${displayWarningEntries.slice(0, 4).map((entry: { provider: string; reasonCode: string; impact?: string; freshness?: string }) => `<li>${escapeHtml(entry.provider.toUpperCase())}: ${escapeHtml(entry.impact || `${entry.provider.toUpperCase()} coverage is reduced.`)}${entry.reasonCode ? ` • ${escapeHtml(entry.reasonCode)}` : ''}${entry.freshness ? ` • ${escapeHtml(entry.freshness)}` : ''}</li>`).join('')}</ul></div>`
        : ''}
      ${evidence.length > 0
        ? `<div class="op-miga-evidence">${evidence.slice(0, 2).map((entry: Record<string, unknown>) => `
            <div class="op-miga-evidence-item">
              <div class="op-miga-evidence-title">${escapeHtml(t(entry.provider).toUpperCase() || 'PROVIDER')} ${escapeHtml(t(entry.entityType).replaceAll('_', ' ') || 'account')}</div>
              <div class="op-miga-evidence-meta">${escapeHtml(t(entry.entityId) || t(entry.providerAccountId) || 'workspace')} • ${escapeHtml(t(entry.freshness) || 'cached')}${t(entry.fetchedAt) ? ` • ${escapeHtml(formatTime(entry.fetchedAt))}` : ''}</div>
            </div>
          `).join('')}</div>`
        : ''}
    </div>
  `
}

export function renderMigaMeta(result: any = {}): string {
  return [
    t(result.status) || 'grounded',
    t(result.freshness) || 'cached',
    t(result.answeredAt) ? `answered ${formatTime(result.answeredAt)}` : '',
    t(result.sourceResolution?.matchedSourceType)
      ? `source ${t(result.sourceResolution.matchedSourceType).replaceAll('_', ' ')}`
      : ''
  ].filter(Boolean).join(' • ')
}

export function renderBlockedPortal(configStatus: { missing?: string[]; guidance?: string[]; status?: string; canRead?: boolean; canWrite?: boolean }): string {
  return `
    ${portalStyles()}
    <div class="operator-portal">
      <div class="op-shell">
        <section class="op-header op-hero-card">
          <div class="op-eyebrow">Hosted Control Plane</div>
          <h1 class="op-title">Hosted setup is blocked right now.</h1>
          <div class="op-subtitle">${escapeHtml(formatHostedConfigMessage(configStatus, { label: 'Hosted operator access' }))}</div>
          <div class="op-alert">
            Missing: ${escapeHtml((configStatus.missing || []).join(', ') || 'unknown prerequisites')}
          </div>
          <ul class="op-guidance-list">
            ${(configStatus.guidance || []).map((entry: string) => `<li>${escapeHtml(entry)}</li>`).join('')}
          </ul>
        </section>
      </div>
    </div>
  `
}

export function renderJoinPortal({ inviteCode = '', error = '' }: { inviteCode?: string, error?: string } = {}) {
  return `
    ${portalStyles()}
    <div class="operator-portal">
      <div class="op-shell">
        <section class="op-header op-hero-card">
          <div class="op-eyebrow">Join Workspace</div>
          <h1 class="op-title">Redeem a workspace invite to enter the operator portal.</h1>
          <div class="op-subtitle">
            This account is signed in, but it is not attached to a hosted workspace yet. Paste an invite code or open a portal link that includes one.
          </div>
          ${error ? `<div class="op-alert">${escapeHtml(error)}</div>` : ''}
          <form class="op-join-form" id="op-join-form">
            <input class="op-input" id="op-join-code" value="${escapeHtml(inviteCode)}" placeholder="Paste invite code" />
            <button class="op-btn primary" id="op-join-submit" type="submit">Join workspace</button>
          </form>
          <div class="op-inline-note">Invite codes are single-purpose and add this actor to the workspace server-side.</div>
        </section>
      </div>
    </div>
  `
}

export function renderInviteResult(invite: any = {}) {
  const inviteCode = t(invite.inviteCode)
  const inviteLink = t(invite.inviteLink)
  return `
    <div class="op-success">Invite created. Share the code or link below.</div>
    <div class="op-invite-result">
      ${inviteCode ? `
        <div class="op-copy-row">
          <div class="op-copy-value">${escapeHtml(inviteCode)}</div>
          <button class="op-btn" type="button" data-op-copy="${escapeHtml(inviteCode)}">Copy code</button>
        </div>
      ` : ''}
      ${inviteLink ? `
        <div class="op-copy-row">
          <div class="op-copy-value">${escapeHtml(inviteLink)}</div>
          <button class="op-btn" type="button" data-op-copy="${escapeHtml(inviteLink)}">Copy link</button>
        </div>
      ` : ''}
    </div>
  `
}

// VAL-WEB-019: Enforce safe protocol allowlist for rendered links
const SAFE_PROTOCOLS = ['https:', 'http:']
export function sanitizeLink(href = '') {
  const value = t(href)
  try {
    const url = new URL(value)
    if (SAFE_PROTOCOLS.includes(url.protocol)) {
      return escapeHtml(url.href)
    }
    // Unsafe protocol - neutralize to blocked anchor
    return '#blocked'
  } catch {
    // Invalid URL - treat as unsafe
    return '#blocked'
  }
}
